import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Terms of Service — working draft for the Saudi market.
// Have a Saudi-licensed lawyer review before publishing.
// ─────────────────────────────────────────────────────────────────────────────

export default function Terms() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const navigate = useNavigate();
  const lastUpdated = 'May 2026';

  return (
    <div className="min-h-screen bg-white text-[#0E1320]" dir={isAr ? 'rtl' : 'ltr'} data-testid="terms-page">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[#E6EAF0]">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[13px] text-[#4A5263] hover:text-[#0E1320]" data-testid="terms-back">
            <ArrowLeft className="h-3.5 w-3.5" />
            {isAr ? 'رجوع' : 'Back'}
          </button>
          <span className="inline-flex items-center gap-1.5 text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#4A5263]">
            <FileText className="h-3.5 w-3.5 text-[color:var(--reva-signal)]" />
            REVA · {isAr ? 'شروط الخدمة' : 'Terms'}
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 prose prose-sm reva-prose">
        <h1 className="text-3xl font-semibold tracking-tight">
          {isAr ? 'شروط الخدمة' : 'Terms of Service'}
        </h1>
        <span
          className="inline-flex items-center gap-1.5 mt-3 text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#4A5263] bg-[#F1F4F7] border border-[#E1E6EC] px-2.5 py-1 rounded-full"
          data-testid="terms-last-updated"
        >
          <FileText className="h-3 w-3 text-[color:var(--reva-signal)]" />
          {isAr ? `آخر تحديث · ${lastUpdated}` : `Last updated · ${lastUpdated}`}
        </span>

        {isAr ? <ArabicBody /> : <EnglishBody />}

        <hr />
        <p className="text-[12px] text-[#71777E]">
          {isAr ? 'لأيّ استفسار قانونيّ تواصل معنا على legal@reva.sa' : 'For any legal inquiry contact legal@reva.sa'}
        </p>
      </main>
    </div>
  );
}

function EnglishBody() {
  return (
    <>
      <h2>1. Acceptance</h2>
      <p>By accessing or using REVA you accept these Terms of Service. If you are using REVA on behalf of an organization you represent that you have the authority to bind that organization.</p>

      <h2>2. The Service</h2>
      <p>REVA is a property operations platform that includes (without limitation) leasing, rent collection, maintenance, accounting, reporting, and tenant communication modules. We may add, modify, or remove features.</p>

      <h2>3. Your account</h2>
      <p>You are responsible for maintaining the confidentiality of your credentials and all activity under your account. Notify us immediately of any unauthorized access.</p>

      <h2>4. Subscription and fees</h2>
      <p>Access to REVA is provided through paid subscription plans. Fees, billing cycle, and renewal terms are set out in your order form. VAT (15% in KSA) is added where applicable.</p>

      <h2>5. Acceptable use</h2>
      <ul>
        <li>Do not use REVA for unlawful purposes.</li>
        <li>Do not attempt to access another organization&rsquo;s data.</li>
        <li>Do not reverse-engineer, decompile, or scrape the platform.</li>
        <li>Do not upload content you do not have the right to upload.</li>
      </ul>

      <h2>6. Customer data</h2>
      <p>You retain ownership of the data you upload (the &ldquo;Customer Data&rdquo;). You grant us a limited license to process your Customer Data solely to provide the Service. We do NOT use your data to train machine-learning models that are shared with other customers.</p>

      <h2>7. Confidentiality</h2>
      <p>Each party will protect the other&rsquo;s confidential information with the same care it uses to protect its own.</p>

      <h2>8. Service levels and support</h2>
      <p>We target 99.5% monthly uptime. Support response times depend on your subscription tier (see your order form).</p>

      <h2>9. Warranty disclaimer</h2>
      <p>The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;. We disclaim all warranties to the maximum extent permitted by law.</p>

      <h2>10. Limitation of liability</h2>
      <p>To the maximum extent permitted by law, our aggregate liability for any claims arising out of these Terms will not exceed the fees you paid us in the 12 months preceding the claim.</p>

      <h2>11. Termination</h2>
      <p>Either party may terminate the Service in accordance with the order form. Upon termination we will give you 30 days to export your data, after which it will be deleted (subject to retention obligations).</p>

      <h2>12. Governing law</h2>
      <p>These Terms are governed by the laws of the Kingdom of Saudi Arabia. Any dispute will be subject to the exclusive jurisdiction of the competent courts of Riyadh.</p>

      <h2>13. Changes</h2>
      <p>We may update these Terms. We will give you at least 30 days&rsquo; notice of material changes.</p>
    </>
  );
}

function ArabicBody() {
  return (
    <>
      <h2>١. القبول</h2>
      <p>باستخدامك منصّة REVA فإنّك توافق على هذه الشّروط. وإذا كنتَ تستخدمها بالنّيابة عن منظّمة فإنّك تقرّ بأنّ لديك الصّلاحيّة لإلزامها.</p>

      <h2>٢. الخدمة</h2>
      <p>REVA منصّة لإدارة عمليّات العقارات تشمل التأجير، التحصيل، الصيانة، المحاسبة، التقارير، والتواصل مع المستأجرين. وقد نضيف ميّزات أو نعدّلها أو نزيلها.</p>

      <h2>٣. الحساب</h2>
      <p>أنت مسؤول عن سرّيّة بياناتك وعن جميع الأنشطة التي تتمّ من خلال حسابك، ويلزمك إخطارنا فور وقوع أيّ وصول غير مصرَّح به.</p>

      <h2>٤. الاشتراك والرّسوم</h2>
      <p>يتمّ الوصول إلى REVA عبر باقات اشتراك مدفوعة. وتُحدَّد الرّسوم ودورة الفوترة وشروط التّجديد في نموذج الطلب المرفق. وتُضاف ضريبة القيمة المضافة (١٥٪ في المملكة) حيثما تنطبق.</p>

      <h2>٥. الاستخدام المقبول</h2>
      <ul>
        <li>لا تستخدم REVA لأيّ غرض غير مشروع.</li>
        <li>لا تحاول الوصول إلى بيانات منظّمة أخرى.</li>
        <li>لا تقم بالهندسة العكسيّة أو استخراج المحتوى أوتوماتيكيًّا.</li>
        <li>لا ترفع محتوى لا تمتلك حقّ رفعه.</li>
      </ul>

      <h2>٦. بيانات العميل</h2>
      <p>تحتفظ بملكيّة البيانات التي ترفعها. وتمنحنا ترخيصًا محدودًا بمعالجة بياناتك فقط لتقديم الخدمة. ولا نستخدم بياناتك لتدريب نماذج تُشارَك مع عملاء آخرين.</p>

      <h2>٧. السّرّية</h2>
      <p>يحمي كلّ طرف معلومات الطّرف الآخر السّرّيّة بنفس درجة العناية التي يحمي بها معلوماته.</p>

      <h2>٨. مستوى الخدمة والدّعم</h2>
      <p>نستهدف توافرًا شهريًّا بنسبة ٩٩٫٥٪. وتعتمد أوقات استجابة الدّعم على باقة الاشتراك (انظر نموذج الطّلب).</p>

      <h2>٩. إخلاء المسؤوليّة</h2>
      <p>تُقدَّم الخدمة «كما هي» و«حسب توفّرها». وننفي جميع الضّمانات إلى أقصى حدّ يسمح به النّظام.</p>

      <h2>١٠. تحديد المسؤوليّة</h2>
      <p>إلى أقصى حدّ يسمح به النّظام، فإنّ إجمالي مسؤوليّتنا عن أيّ مطالبة لن يتجاوز الرّسوم المدفوعة لنا خلال الاثني عشر شهرًا السّابقة للمطالبة.</p>

      <h2>١١. الإنهاء</h2>
      <p>يحقّ لأيّ طرف إنهاء الخدمة وفقًا لنموذج الطّلب. وعند الإنهاء نمنحك ثلاثين يومًا لتصدير بياناتك، ثمّ تُحذف وفق التزامات الحفظ.</p>

      <h2>١٢. القانون الواجب التّطبيق</h2>
      <p>تخضع هذه الشّروط لأنظمة المملكة العربيّة السعوديّة. ويختصّ بنظر أيّ نزاع المحاكم المختصّة في الرّياض حصرًا.</p>

      <h2>١٣. التّعديلات</h2>
      <p>قد نحدّث هذه الشّروط، وسنُخطرك قبل ثلاثين يومًا على الأقلّ من سريان أيّ تعديل جوهريّ.</p>
    </>
  );
}
